/**
 * Starfield Alpine.js Component
 * WebGL fragment-shader tunnel of stars that steers toward the cursor.
 * Brand palette: lime / indigo / zinc with occasional hot-white sparkles.
 */

import { ShaderRenderer } from './utils/shader-renderer.js';
import { observeFooterReveal } from './utils/footer-reveal.js';

// GLSL template literal — do not add backticks or ${ to this string without escaping.
const FRAG = `
precision highp float;
uniform vec2  u_res;
uniform float u_time;
uniform vec2  u_mouse;
uniform vec2  u_click;
uniform float u_clickTime;

#define PI 3.14159265359
#define TAU 6.28318530718

float hash11(float p){ p = fract(p*.1031); p *= p+33.33; p *= p+p; return fract(p); }
float hash12(vec2 p){ vec3 p3 = fract(vec3(p.xyx)*.1031); p3 += dot(p3, p3.yzx+33.33); return fract((p3.x+p3.y)*p3.z); }
vec2  hash22(vec2 p){ vec3 p3 = fract(vec3(p.xyx)*vec3(.1031,.1030,.0973)); p3 += dot(p3, p3.yzx+33.33); return fract((p3.xx+p3.yz)*p3.zy); }

float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f*f*(3.0-2.0*f);
  return mix(mix(hash12(i+vec2(0,0)), hash12(i+vec2(1,0)), u.x),
             mix(hash12(i+vec2(0,1)), hash12(i+vec2(1,1)), u.x), u.y);
}
float fbm(vec2 p){
  float v=0.0, a=0.5;
  for(int i=0;i<6;i++){ v+=a*noise(p); p*=2.02; a*=0.5; }
  return v;
}

mat2 rot(float a){ float c=cos(a), s=sin(a); return mat2(c,-s,s,c); }

vec2 hash22w(vec2 p, float period){
  p.x = mod(p.x, period);
  return hash22(p);
}
float hash12w(vec2 p, float period){
  p.x = mod(p.x, period);
  return hash12(p);
}

void main(){
  vec2 uv = (gl_FragCoord.xy - 0.5*u_res) / min(u_res.x, u_res.y);
  vec2 m  = (u_mouse - 0.5);

  vec2 center = m * 0.6;
  vec2 p = uv - center;

  float a = atan(p.y, p.x);
  float r = length(p);

  float z = u_time * 0.6 + 0.5 / max(r, 0.03);
  float theta = a / TAU + 0.5;
  const float THETA_CELLS = 24.0;

  vec2 grid = vec2(theta * THETA_CELLS, z * 4.0);
  vec2 ig = floor(grid), fg = fract(grid);
  vec3 col = vec3(0.0);

  for(int dy=-1; dy<=1; dy++){
    for(int dx=-1; dx<=1; dx++){
      vec2 o = vec2(float(dx), float(dy));
      vec2 cell = ig + o;
      vec2 jitter = hash22w(cell, THETA_CELLS);
      vec2 sp = o + jitter - fg;
      float d = length(sp);
      float br = hash12w(cell + 17.0, THETA_CELLS);
      float star = smoothstep(0.35, 0.0, d) * (0.3 + 0.7*br);
      star *= smoothstep(0.0, 0.25, r) * smoothstep(1.2, 0.3, r);
      vec3 cLime   = vec3(0.247, 0.857, 0.000);
      vec3 cIndigo = vec3(0.443, 0.506, 1.000);
      vec3 cZinc7  = vec3(0.247, 0.247, 0.275);
      vec3 hue = mix(cIndigo, cLime, br);
      hue = mix(hue, cZinc7 * 2.0, 0.35);
      hue = mix(hue, vec3(1.0), pow(br, 6.0) * 0.6);
      col += hue * star;
    }
  }

  vec3 cZinc9 = vec3(0.094, 0.094, 0.106);
  vec3 cZinc7 = vec3(0.247, 0.247, 0.275);
  float holeR = 0.13;
  float hole  = 1.0 - smoothstep(holeR*0.6, holeR, r);
  col *= smoothstep(holeR*0.4, holeR*1.05, r);

  float wall = smoothstep(0.02, 0.0, r) * 1.2;
  vec3 wallCol = mix(vec3(0.443, 0.506, 1.000), cZinc7, 0.75);
  col += wallCol * wall * (1.0 - hole);

  float bands = 0.5 + 0.5*sin(a*6.0 + z*3.0);
  col += mix(vec3(0.152, 0.152, 0.165), cZinc7, 0.65) * bands * smoothstep(0.0, 0.3, r) * 0.5;

  col = mix(col, vec3(0.0), hole);

  float clickAge = u_time - u_clickTime;
  if(clickAge < 1.5){
    float ring = exp(-abs(r - clickAge*0.8)*8.0);
    vec3 burstCol = mix(vec3(0.443, 0.506, 1.000), vec3(0.247, 0.857, 0.000), smoothstep(0.0, 0.6, clickAge));
    col += burstCol * exp(-clickAge*3.0) * ring;
  }

  col *= 1.0 - 0.4 * smoothstep(0.6, 1.2, length(uv));

  gl_FragColor = vec4(col, 1.0);
}
`;

export default () => ({
    renderer: null,

    init() {
        if (!window.matchMedia('(min-width: 768px)').matches) return;
        const canvas = this.$refs.canvas;
        if (!canvas) return;

        try {
            this.renderer = new ShaderRenderer(canvas);
            this.renderer.setShader(FRAG);
            this.$el.classList.remove('opacity-0');
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.renderer.frame();
            } else {
                this.stopObservingFooter = observeFooterReveal((revealed) => revealed ? this.renderer?.start() : this.renderer?.stop());
            }
        } catch (e) {
            this.renderer = null;
        }
    },

    destroy() {
        this.stopObservingFooter?.();
        this.renderer?.destroy();
        this.renderer = null;
    },
});
